import {
  getAttractions,
  getAttractionOffers,
  getBookingDetailsByIds,
  getExperienceById,
  getExperienceCategories,
  getExperienceDateAvailability,
  getExperienceListFacets,
  getExperiences,
  getExperiencesById,
  getExperienceSearchList,
  getExperienceTimeAvailability,
  getExperienceTransferAvailability,
  getExperienceVoucher,
  validateExperiencesItems,
  type ExperienceFacetFilters,
} from 'api/experiences'
import { EXPERIENCE_VIEWED, API_CALL, API_CALL_SUCCESS, SET_EXPERIENCE_PLACE, UPDATE_USER_LAT_LONG, SET_CANCELLING_EXPERIENCES, CLEAR_EXPERIENCE_TIMES, CLEAR_EXPERIENCE, OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA } from './actionConstants'
import { getAttractionOffersKey, getExperienceListKey, getExperienceDatesKey, getExperienceTimesKey, type ExperienceCoordinates } from 'lib/experiences/experienceUtils'
import {
  FETCH_EXPERIENCE,
  FETCH_EXPERIENCE_LIST,
  FETCH_EXPERIENCE_DATES,
  FETCH_EXPERIENCE_TIMESLOTS,
  FETCH_EXPERIENCES,
  FETCH_EXPERIENCE_CATEGORIES,
  FETCH_EXPERIENCE_LIST_FACETS,
  FETCH_EXPERIENCE_VOUCHER,
  FETCH_BOOKING_DETAILS,
  FETCH_TRANSFER_OPTIONS,
  FETCH_REFUND_DETAILS,
  VALIDATE_EXPERIENCE_ITEMS,
  FETCH_TOP_ATTRACTIONS,
  FETCH_ATTRACTION_OFFERS,
} from './apiActionConstants'
import { addRecentlyViewedExperience } from 'cookies/recentlyViewedExperiencesCookie'
import { unique } from 'lib/array/arrayUtils'
import { getPlaceByRegionCode } from 'constants/places'
import { clearExperienceLocationCookie, setExperienceLocationCookie } from 'cookies/experienceLocationCookie'
import getCurrentGeoLocation from 'lib/geo/navigatorUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getOrderItemsRefundDetails } from 'api/order'
import type { AppAction } from './types/ActionTypes'
import type { OrderExperienceViewTicket } from 'components/Common/OrderView/OrderItemSummaries/OrderExperienceSummaries/OrderExperienceSummary'
import pollExperiencesVouchers from 'lib/polling/pollExperiencesVouchers'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getObjectKey from 'lib/object/getObjectKey'

export function setExperienceOffersMetadata(listKey: string, metadata: App.ExperienceOffersMetadata): AppAction {
  return {
    type: OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA,
    listKey,
    metadata,
  }
}

export function fetchExperienceSearchList(
  placeId: string | undefined,
  filters: {
    categoryCodes?: Array<number>
    priceLte?: number
    priceGte?: number
    sortBy?: Experiences.SortTypes
    campaigns?: Array<string>
    placeIdToIgnore?: string
    bounds?: string
    luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
    leExclusive?: boolean
    collections?: Array<string>
    from?: string
    to?: string
    minRating?: number
    targetAudience?: Array<App.ExperienceTargetAudience>
    hasFreeCancellation?: boolean
    hasInstantConfirmation?: boolean
    isGift?: boolean
    coordinatesList?: Array<ExperienceCoordinates>
    timeSlots?: Array<Experiences.TimeSlotFilter>
  } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getExperienceListKey({ placeId, ...filters })

    if (state.experience.experienceLists[key]) {
      return // already fetching or have result for this key
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST,
      request: async() => {
        const { experienceIds, offersMetadata } = await getExperienceSearchList({
          placeId,
          region: state.geo.currentRegionCode,
          ...filters,
          luxPlusFeatures: filters.luxPlusFeatures,
        })

        if (offersMetadata) {
          dispatch(setExperienceOffersMetadata(key, offersMetadata))
        }

        return { experienceIds }
      },
      key,
    })
  }
}

export interface ExperienceListFilters {
  latitude?: number
  longitude?: number
  currencyCode?: string
  categoryCodes?: Array<number>
  categoryCodesNotIn?: Array<number>
  distanceRange?: Array<string>
  from?: string
  to?: string
  postPurchaseOnly?: boolean
  offerId?: string
  showUnlisted?: boolean
  limit?: number
  offset?: number
  targetAudienceIn?: Array<App.ExperienceTargetAudience>
  onlyCuratedByRegion?: boolean
  hasMemberPrice?: boolean
  experienceIds?: Array<string>
  experienceIdsNotIn?: Array<string>
  bundledPackageId?: string
  isLoyaltyEligible?: boolean
  tagIds?: Array<string>
}

export function fetchExperienceList(
  filters: ExperienceListFilters = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { airports, currentRegionCode } = state.geo
    const currentCurrency = filters.currencyCode ?? state.geo.currentCurrency
    const key = getExperienceListKey(filters)

    if (state.experience.experienceLists[key]) {
      return // already fetching or have result for this key
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST,
      request: async() => {
        const { experiences, total } = await getExperiences({
          currentCurrency,
          filters: {
            latitude: filters.latitude,
            longitude: filters.longitude,
            categoryCodes: filters.categoryCodes,
            categoryCodesNotIn: filters.categoryCodesNotIn,
            distanceRange: filters.distanceRange,
            from: filters.from,
            to: filters.to,
            postPurchaseOnly: filters.postPurchaseOnly,
            hotelOfferId: filters.offerId,
            showUnlisted: filters.showUnlisted,
            limit: filters.limit,
            offset: filters.offset,
            targetAudienceIn: filters.targetAudienceIn,
            onlyCuratedByRegion: filters.onlyCuratedByRegion,
            hasMemberPrice: filters.hasMemberPrice,
            experienceIds: filters.experienceIds,
            experienceIdsNotIn: filters.experienceIdsNotIn,
            bundledPackageId: filters.bundledPackageId,
            isLoyaltyEligible: filters.isLoyaltyEligible,
            tagIds: filters.tagIds,
          },
          airports,
          currentRegionCode,
        })
        const experienceIds = experiences.map(exp => exp.id)

        dispatch({
          type: API_CALL_SUCCESS,
          api: FETCH_EXPERIENCES,
          data: { experiences, errors: {} },
          ids: experienceIds,
        })

        return { experienceIds, total }
      },

      key,
    })
  }
}

export function fetchExperiencesById(...ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = unique(ids.filter(id => !state.experience.experiences[id] && !state.experience.fetchingExperiences[id] && !state.experience.experienceErrors[id]))

    if (missingIds.length === 0) {
      return // all requested ids already in state (fetched, fetching, or errored)
    }

    const { currentCurrency, airports, currentRegionCode, locale } = state.geo

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCES,
      request: () => getExperiencesById(missingIds, { currentCurrency, currentRegionCode, airports, locale }),
      ids: missingIds,
    })
  }
}

export function clearExperienceById(id: string) {
  return { type: CLEAR_EXPERIENCE, id } as const
}

export function fetchExperienceById(id: string, localeOverride?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.experience.experiences[id] || state.experience.fetchingExperiences[id] || state.experience.experienceErrors[id]) {
      return // already have it, are currently fetching it, or a previous fetch errored
    }

    const { currentCurrency, airports, currentRegionCode, locale } = state.geo

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE,
      request: () => getExperienceById(id, {
        airports,
        currentRegionCode,
        currentCurrency,
        locale: localeOverride ?? locale,
      }),
      id,
    })
  }
}

interface FetchDatesParams {
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function fetchExperienceDates(experienceId: string, params?: FetchDatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getExperienceDatesKey(experienceId, {
      pickupPointId: params?.pickupPointId,
      redemptionLocationId: params?.redemptionLocationId,
      tickets: params?.tickets,
      ticketModesMap: params?.ticketModesMap,
      orderId: params?.orderId,
    })

    if (state.experience.experienceDates[experienceId]?.[key]) return
    dispatch({
      key,
      experienceId,
      type: API_CALL,
      api: FETCH_EXPERIENCE_DATES,
      request: () => getExperienceDateAvailability(experienceId, {
        pickupPointId: params?.pickupPointId,
        redemptionLocationId: params?.redemptionLocationId,
        tickets: params?.tickets,
        ticketModesMap: params?.ticketModesMap,
        orderId: params?.orderId,
      }),
    })
  }
}

interface FetchTimesParams {
  currency?: string
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  date?: string
  isBuyNowBookLater?: boolean
  isGift?: boolean
  ignoreCache?: boolean
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function fetchExperienceTimes(
  experienceId: string,
  params: FetchTimesParams = EmptyObject,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currencyCode = params.currency ?? state.geo.currentCurrency
    const customerId = state.auth.account.memberId
    const key = getExperienceTimesKey(experienceId, params.date, {
      currency: currencyCode,
      pickupPointId: params.pickupPointId,
      redemptionLocationId: params.redemptionLocationId,
      isBuyNowBookLater: params.isBuyNowBookLater,
      isGift: params.isGift,
      tickets: params.tickets,
      ticketModesMap: params.ticketModesMap,
      orderId: params.orderId,
    })

    if (!params.ignoreCache && state.experience.experienceTimes[experienceId]?.[key]) return

    dispatch({
      key,
      experienceId,
      type: API_CALL,
      api: FETCH_EXPERIENCE_TIMESLOTS,
      request: () => getExperienceTimeAvailability(experienceId, {
        currencyCode,
        pickupPointId: params.pickupPointId,
        redemptionLocationId: params.redemptionLocationId,
        customerId,
        tickets: params.tickets,
        ticketModesMap: params.ticketModesMap,
        date: params.date,
        orderId: params.orderId,
      }),
    })
  }
}

export function clearExperienceTimes(experienceId: string): AppAction {
  return {
    type: CLEAR_EXPERIENCE_TIMES,
    experienceId,
  }
}

export function fetchExperienceCategories(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const locale = state.geo.locale
    const cachedForCurrentLocale = state.experience.experienceCategoriesLocale === locale
    if ((state.experience.experienceCategories.length > 0 && cachedForCurrentLocale) ||
      state.experience.fetchingCategories ||
      (state.experience.experienceCategoriesError && cachedForCurrentLocale)) {
      return // already loaded for this locale, loading, or errored for this locale
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_CATEGORIES,
      request: () => getExperienceCategories(locale),
      locale,
    })
  }
}

export function fetchExperienceListFacets(placeId: string, filters: ExperienceFacetFilters = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const facetKey = getObjectKey({ placeId, ...filters })
    if (state.experience.experienceListFacets[facetKey]) {
      return // already fetching or have facets for this place+filters
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST_FACETS,
      request: () => getExperienceListFacets(placeId, state.geo.currentRegionCode, filters),
      key: facetKey,
    })
  }
}

export function fetchExperienceVoucher(experienceItemId: string): AppAction {
  return {
    type: API_CALL,
    experienceItemId,
    api: FETCH_EXPERIENCE_VOUCHER,
    request: () => getExperienceVoucher(experienceItemId),
  }
}

export function fetchBookingDetailsByIds(ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = ids.filter(id =>
      !state.experience.bookingDetails[id] &&
      !state.experience.fetchingBookingDetails[id] &&
      !state.experience.bookingDetailsErrors[id],
    )
    if (!missingIds.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_BOOKING_DETAILS,
      request: () => getBookingDetailsByIds(missingIds),
      ids: missingIds,
    })
  }
}

export function fetchRefundDetailsByIds(ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = ids.filter(id =>
      !state.experience.refundDetails[id] &&
      !state.experience.fetchingRefundDetails[id] &&
      !state.experience.refundDetailsErrors[id],
    )
    if (!missingIds.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_REFUND_DETAILS,
      request: () => getOrderItemsRefundDetails(ids),
      ids,
    })
  }
}

export function experienceViewed(experience: App.ExperienceOffer): AppAction {
  addRecentlyViewedExperience(experience.id)
  return {
    type: EXPERIENCE_VIEWED,
    id: experience.id,
    experience,
  }
}

interface ExperiencePlaceOptions {
  placeId?: string
  regionCode?: string
  userSelected?: boolean
  save?: boolean
}

export function setExperiencePlace(type: App.ExperiencePlaceType, options: ExperiencePlaceOptions = {}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const geo = state.geo

    let placeId: string | undefined
    switch (type) {
      case 'region':
        placeId = options.placeId ?? getPlaceByRegionCode(options.regionCode ?? geo.currentRegionCode).id
        break
      case 'place':
        placeId = options.placeId
        break
      case 'currentLocation':
        try {
          const coords = await getCurrentGeoLocation()
          dispatch({
            type: UPDATE_USER_LAT_LONG,
            latitude: coords.latitude,
            longitude: coords.longitude,
          })
        } catch(message) {
          const intl = getCurrentIntl()
          showSnackbar(message, 'warning', {
            heading: intl.formatMessage({
              id: '7Ve2jl21TjZUihrVgd19',
              defaultMessage: 'Couldn’t find your location!',
              description: 'Snackbar heading when geolocation fails for experiences',
            }),
          })
          return
        }
        break
    }

    if (options.save) {
      setExperienceLocationCookie(type, placeId, options.userSelected)
    } else {
      clearExperienceLocationCookie()
    }

    dispatch({
      type: SET_EXPERIENCE_PLACE,
      placeType: type,
      placeId,
      userSelected: !!options.userSelected,
      save: options.save,
    })
  }
}

export function fetchTransferOptions(experienceId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const existingState = state.experience.experienceTransferOptions[experienceId]
    if (existingState && (existingState.fetching || existingState.error || existingState.transfers)) {
      return
    }
    const customerId = state.auth.account.memberId

    dispatch({
      type: API_CALL,
      api: FETCH_TRANSFER_OPTIONS,
      request: async() => {
        const dates = await getExperienceDateAvailability(experienceId)
        const firstNotSoldOutDate = dates.find(d => !d.soldOut)
        if (!firstNotSoldOutDate) {
          throw new Error('No dates available for transfer')
        }
        return await getExperienceTransferAvailability(experienceId, firstNotSoldOutDate.day, { currencyCode: state.geo.currentCurrency, customerId })
      },
      experienceId,
    })
  }
}

export function setCancellingExperiences(experiences: Array<OrderExperienceViewTicket>): AppAction {
  return {
    type: SET_CANCELLING_EXPERIENCES,
    experiences: experiences.map(view => view.item),
  }
}

export function pollingExperienceVouchers(experienceItemIds: Array<string>): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const pendingItems = experienceItemIds.filter(id => {
      const fetching = state.experience.fetchingBookingDetails[id]
      const error = state.experience.bookingDetailsErrors[id]
      const bookingDetails = state.experience.bookingDetails[id]

      return !fetching && !error && !bookingDetails
    })

    if (!pendingItems.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_BOOKING_DETAILS,
      request: () => pollExperiencesVouchers({
        experienceItemIds: pendingItems,
        maxTime: 1000 * 60 * 2, // 2 minutes
        pollInterval: 1000 * 3, // 3 seconds
      }),
      ids: pendingItems,
    })
  }
}

export function fetchTopAttractions(params: { placeName?: string; placeType?: string; lePlaceId?: string; countryCode?: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = params.lePlaceId
      ? `top-attractions-place-${params.lePlaceId}`
      : params.countryCode
        ? `top-attractions-country-${params.countryCode}`
        : undefined

    if (!key) return

    const cached = state.experience.topAttractions[key]
    if (cached?.fetching) return
    if (cached && !cached.error && cached.lastFetchedAt && Date.now() - cached.lastFetchedAt < 30_000) return

    dispatch({
      type: API_CALL,
      api: FETCH_TOP_ATTRACTIONS,
      request: async() => {
        const result = await getAttractions({ ...params, sort: 'popularity', limit: 10 })
        return result
      },
      key,
    })
  }
}

export function fetchAttractionOffers(attractionId: string, searchFilters?: Experiences.SearchFilters): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getAttractionOffersKey(attractionId, searchFilters)

    const cachedOffers = state.experience.attractionOffers?.[key]
    if (cachedOffers?.fetching) return
    if (cachedOffers && !cachedOffers.error && cachedOffers.lastFetchedAt && Date.now() - cachedOffers.lastFetchedAt < 30_000) return

    dispatch({
      type: API_CALL,
      api: FETCH_ATTRACTION_OFFERS,
      request: () => getAttractionOffers({
        attractionId,
        limit: 50,
        categoryValues: searchFilters?.categoryCodes,
        luxPlusExclusive: searchFilters?.luxPlusFeatures?.length ? true : undefined,
        priceLte: searchFilters?.priceLte,
        priceGte: searchFilters?.priceGte,
        availableFrom: searchFilters?.checkIn,
        availableTo: searchFilters?.checkOut,
      }),
      key,
    })
  }
}

export function validateExperiencesCheckoutItems(payload: Experiences.ValidateExperienceItemsPayload): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const allFetchedOrFetching = payload.bookingItems
      .every((item) => state.experience.experienceItemsValidation[item.experienceItemId]?.transactionKey === item.transactionKey)

    // Skip if already fetched or currently fetching
    if (allFetchedOrFetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: VALIDATE_EXPERIENCE_ITEMS,
      bookingItems: payload.bookingItems,
      request: () => validateExperiencesItems(payload),
    })
  }
}
